import React, { useState, useEffect, useRef } from "react";
import {
  FaTimes,
  FaSave,
  FaBuilding,
  FaUser,
  FaCalendarAlt,
  FaTag,
  FaHistory,
  FaInfoCircle,
  FaPaperclip,
  FaCloudUploadAlt,
  FaDownload,
  FaTrash,
  FaFilePdf,
  FaFileImage,
  FaFileWord,
  FaFileExcel,
  FaFileAlt,
  FaFile,
} from "react-icons/fa";
import axios from "axios";
import { API_BASE_URL } from "../../config";

// File format helper
const formatFileSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// File icon helper
const getFileIcon = (mimeType, fileName) => {
  const ext = (fileName || "").split(".").pop().toLowerCase();
  const m = (mimeType || "").toLowerCase();

  if (m.includes("pdf") || ext === "pdf")
    return <FaFilePdf style={{ color: "#ef4444", fontSize: "20px" }} />;
  if (m.includes("image") || ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext))
    return <FaFileImage style={{ color: "#10b981", fontSize: "20px" }} />;
  if (m.includes("word") || ["doc", "docx"].includes(ext))
    return <FaFileWord style={{ color: "#0284c7", fontSize: "20px" }} />;
  if (m.includes("excel") || m.includes("sheet") || ["xls", "xlsx", "csv"].includes(ext))
    return <FaFileExcel style={{ color: "#16a34a", fontSize: "20px" }} />;
  if (m.includes("text") || ["txt", "log", "md"].includes(ext))
    return <FaFileAlt style={{ color: "#64748b", fontSize: "20px" }} />;
  return <FaFile style={{ color: "#94a3b8", fontSize: "20px" }} />;
};

const TaskModal = ({ isOpen, onClose, task, onSave }) => {
  const [activeTab, setActiveTab] = useState("details");
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  // Attachments State
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [attachmentError, setAttachmentError] = useState("");
  const fileInputRef = useRef(null);

  // Dropdown lists
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "To Do",
    priority: "Medium",
    assigned_to: "",
    assigned_to_id: null,
    customer_id: null,
    client_name: "",
    deal_reference: "",
    due_date: "",
    start_date: "",
    category: "General",
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Fetch customers & employees for linking
    const token = localStorage.getItem("access_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    axios
      .get(`${API_BASE_URL}/customers/dropdown`, { headers })
      .then((res) => {
        if (Array.isArray(res.data)) {
          setCustomers(res.data);
        }
      })
      .catch((err) => console.log("Failed to load customers for dropdown", err));

    axios
      .get(`${API_BASE_URL}/employees/`, { headers })
      .then((res) => {
        if (Array.isArray(res.data)) {
          setEmployees(res.data);
        }
      })
      .catch((err) => console.log("Failed to load employees", err));
  }, []);

  const loadAttachments = (taskId) => {
    setLoadingAttachments(true);
    const token = localStorage.getItem("access_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    axios
      .get(`${API_BASE_URL}/tasks/${taskId}/attachments`, { headers })
      .then((res) => setAttachments(res.data || []))
      .catch(() => setAttachments([]))
      .finally(() => setLoadingAttachments(false));
  };

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || "",
        description: task.description || "",
        status: task.status || "To Do",
        priority: task.priority || "Medium",
        assigned_to: task.assigned_to || "",
        assigned_to_id: task.assigned_to_id || null,
        customer_id: task.customer_id || null,
        client_name: task.client_name || "",
        deal_reference: task.deal_reference || "",
        due_date: task.due_date ? task.due_date.substring(0, 10) : "",
        start_date: task.start_date ? task.start_date.substring(0, 10) : "",
        category: task.category || "General",
      });

      // Load activities & attachments if existing task
      if (task.id) {
        setLoadingActivities(true);
        const token = localStorage.getItem("access_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        axios
          .get(`${API_BASE_URL}/tasks/${task.id}/activities`, { headers })
          .then((res) => setActivities(res.data || []))
          .catch(() => setActivities([]))
          .finally(() => setLoadingActivities(false));

        loadAttachments(task.id);
      } else {
        setAttachments([]);
      }
    } else {
      setFormData({
        title: "",
        description: "",
        status: "To Do",
        priority: "Medium",
        assigned_to: "",
        assigned_to_id: null,
        customer_id: null,
        client_name: "",
        deal_reference: "",
        due_date: new Date().toISOString().substring(0, 10),
        start_date: new Date().toISOString().substring(0, 10),
        category: "General",
      });
      setActivities([]);
      setAttachments([]);
      setPendingFiles([]);
    }
    setAttachmentError("");
    setActiveTab("details");
  }, [task, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomerChange = (e) => {
    const custId = e.target.value ? parseInt(e.target.value, 10) : null;
    const selected = customers.find((c) => c.id === custId);
    setFormData((prev) => ({
      ...prev,
      customer_id: custId,
      client_name: selected ? selected.name : prev.client_name,
    }));
  };

  const handleEmployeeChange = (e) => {
    const empId = e.target.value ? parseInt(e.target.value, 10) : null;
    const selected = employees.find((emp) => emp.id === empId);
    setFormData((prev) => ({
      ...prev,
      assigned_to_id: empId,
      assigned_to: selected
        ? `${selected.first_name || ""} ${selected.last_name || ""}`.trim()
        : prev.assigned_to,
    }));
  };

  // Upload attachment for existing task
  const handleUploadAttachment = async (file) => {
    if (!file) return;
    if (!task || !task.id) {
      // New task: stage into pendingFiles
      setPendingFiles((prev) => [...prev, file]);
      return;
    }

    setUploadingAttachment(true);
    setAttachmentError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const user = localStorage.getItem("username") || "User";
      fd.append("uploaded_by", user);

      const token = localStorage.getItem("access_token");
      const headers = token
        ? { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        : { "Content-Type": "multipart/form-data" };

      const res = await axios.post(`${API_BASE_URL}/tasks/${task.id}/attachments`, fd, { headers });
      setAttachments((prev) => [res.data, ...prev]);
    } catch (err) {
      console.error("Error uploading attachment:", err);
      setAttachmentError("Failed to upload attachment. Please try again.");
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Delete attachment
  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm("Are you sure you want to delete this attachment?")) return;
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API_BASE_URL}/tasks/${task.id}/attachments/${attachmentId}`, { headers });
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      console.error("Error deleting attachment:", err);
      alert("Failed to delete attachment.");
    }
  };

  // Submit Task (and any pending attachments if newly created)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert("Please provide a task title.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
      };
      const savedTask = await onSave(payload, task ? task.id : null);

      // If pending files exist and we have a task ID (from newly created task)
      const targetId = (savedTask && savedTask.id) || (task && task.id);
      if (targetId && pendingFiles.length > 0) {
        const token = localStorage.getItem("access_token");
        const headers = token
          ? { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
          : { "Content-Type": "multipart/form-data" };
        const user = localStorage.getItem("username") || "User";

        for (const f of pendingFiles) {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("uploaded_by", user);
          await axios.post(`${API_BASE_URL}/tasks/${targetId}/attachments`, fd, { headers });
        }
      }

      onClose();
    } catch (err) {
      console.error("Error saving task:", err);
      alert("Failed to save task. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const totalAttachments = task && task.id ? attachments.length : pendingFiles.length;

  return (
    <div className="tm-modal-backdrop" onClick={onClose}>
      <div className="tm-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="tm-modal-header">
          <h4>{task && task.id ? "Edit Task" : "Create New Task"}</h4>
          <button className="tm-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Tab switch */}
        <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
          <button
            type="button"
            onClick={() => setActiveTab("details")}
            style={{
              padding: "10px 20px",
              border: "none",
              background: activeTab === "details" ? "#fff" : "transparent",
              fontWeight: 600,
              fontSize: "13px",
              color: activeTab === "details" ? "#0284c7" : "#64748b",
              borderBottom: activeTab === "details" ? "2px solid #0284c7" : "none",
              cursor: "pointer",
            }}
          >
            Task Details
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("attachments")}
            style={{
              padding: "10px 20px",
              border: "none",
              background: activeTab === "attachments" ? "#fff" : "transparent",
              fontWeight: 600,
              fontSize: "13px",
              color: activeTab === "attachments" ? "#0284c7" : "#64748b",
              borderBottom: activeTab === "attachments" ? "2px solid #0284c7" : "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <FaPaperclip /> Attachments ({totalAttachments})
          </button>

          {task && task.id && (
            <button
              type="button"
              onClick={() => setActiveTab("history")}
              style={{
                padding: "10px 20px",
                border: "none",
                background: activeTab === "history" ? "#fff" : "transparent",
                fontWeight: 600,
                fontSize: "13px",
                color: activeTab === "history" ? "#0284c7" : "#64748b",
                borderBottom: activeTab === "history" ? "2px solid #0284c7" : "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <FaHistory /> Activity History ({activities.length})
            </button>
          )}
        </div>

        {/* Modal Body */}
        <div className="tm-modal-body">
          {activeTab === "details" ? (
            <form id="task-form" onSubmit={handleSubmit}>
              <div className="tm-form-group full-width">
                <label>
                  Task Title <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  placeholder="e.g., Follow up with ABC Company regarding proposal"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="tm-form-grid">
                {/* Status */}
                <div className="tm-form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="To Do">To Do</option>
                    <option value="In Progress">In Progress</option>
                    <option value="In Review">In Review</option>
                    <option value="Completed">Completed</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Priority */}
                <div className="tm-form-group">
                  <label>Priority</label>
                  <select name="priority" value={formData.priority} onChange={handleChange}>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="tm-form-grid">
                {/* Client Linking */}
                <div className="tm-form-group">
                  <label>
                    <FaBuilding style={{ marginRight: 6, color: "#10b981" }} />
                    Link to Client / Contact
                  </label>
                  <select
                    name="customer_id"
                    value={formData.customer_id || ""}
                    onChange={handleCustomerChange}
                  >
                    <option value="">-- Choose Existing CRM Client --</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.code ? `(${c.code})` : ""}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    name="client_name"
                    placeholder="Or type custom client name"
                    value={formData.client_name}
                    onChange={handleChange}
                    style={{ marginTop: "6px" }}
                  />
                  <span className="tm-badge-helper">
                    Connecting tasks to clients makes your CRM context-rich.
                  </span>
                </div>

                {/* Task Assignment */}
                <div className="tm-form-group">
                  <label>
                    <FaUser style={{ marginRight: 6, color: "#0284c7" }} />
                    Assign to Team Member
                  </label>
                  <select
                    name="assigned_to_id"
                    value={formData.assigned_to_id || ""}
                    onChange={handleEmployeeChange}
                  >
                    <option value="">-- Select Employee --</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} ({emp.employee_id || emp.email || "Staff"})
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    name="assigned_to"
                    placeholder="Or type assignee name"
                    value={formData.assigned_to}
                    onChange={handleChange}
                    style={{ marginTop: "6px" }}
                  />
                </div>
              </div>

              <div className="tm-form-grid">
                {/* Due Date */}
                <div className="tm-form-group">
                  <label>
                    <FaCalendarAlt style={{ marginRight: 6, color: "#ef4444" }} />
                    Due Date
                  </label>
                  <input
                    type="date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleChange}
                  />
                </div>

                {/* Start Date */}
                <div className="tm-form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="tm-form-grid">
                {/* Deal / Inquiry Reference */}
                <div className="tm-form-group">
                  <label>Deal / Inquiry Reference</label>
                  <input
                    type="text"
                    name="deal_reference"
                    placeholder="e.g., Deal #1042 / Inquiry #88"
                    value={formData.deal_reference}
                    onChange={handleChange}
                  />
                </div>

                {/* Category */}
                <div className="tm-form-group">
                  <label>Category</label>
                  <select name="category" value={formData.category} onChange={handleChange}>
                    <option value="Follow-up">Follow-up</option>
                    <option value="Client Meeting">Client Meeting</option>
                    <option value="Proposal / Quotation">Proposal / Quotation</option>
                    <option value="Contract / Closing">Contract / Closing</option>
                    <option value="Billing & Invoicing">Billing & Invoicing</option>
                    <option value="General">General</option>
                  </select>
                </div>
              </div>

              {/* Description */}
              <div className="tm-form-group full-width">
                <label>Description & Notes</label>
                <textarea
                  name="description"
                  rows="3"
                  placeholder="Add details, instructions, or agenda for this CRM task..."
                  value={formData.description}
                  onChange={handleChange}
                ></textarea>
              </div>

              {/* Quick attachment preview in details tab if any attachments exist */}
              {totalAttachments > 0 && (
                <div style={{ marginTop: "12px", padding: "10px 14px", background: "#f0f9ff", borderRadius: "8px", border: "1px solid #bae6fd", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "12px", color: "#0284c7", fontWeight: 600 }}>
                    <FaPaperclip style={{ marginRight: 6 }} />
                    {totalAttachments} file attachment{totalAttachments > 1 ? "s" : ""} linked to this task
                  </span>
                  <button
                    type="button"
                    onClick={() => setActiveTab("attachments")}
                    style={{ background: "#0284c7", color: "#fff", border: "none", borderRadius: "5px", padding: "4px 10px", fontSize: "11px", fontWeight: 600, cursor: "pointer" }}
                  >
                    View / Manage
                  </button>
                </div>
              )}
            </form>
          ) : activeTab === "attachments" ? (
            /* ATTACHMENTS TAB */
            <div>
              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleUploadAttachment(e.target.files[0]);
                  }
                }}
              />

              {/* Upload Dropzone */}
              <div
                className="tm-attachment-dropzone"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                    handleUploadAttachment(e.dataTransfer.files[0]);
                  }
                }}
              >
                <FaCloudUploadAlt />
                <div style={{ fontWeight: 600, fontSize: "14px", color: "#0284c7" }}>
                  {uploadingAttachment ? "Uploading file..." : "Click or Drag & Drop file here"}
                </div>
                <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                  Upload documents, contracts, images, PDFs, spreadsheets, or receipts
                </div>
              </div>

              {attachmentError && (
                <div style={{ color: "#ef4444", fontSize: "12px", marginBottom: "12px" }}>
                  {attachmentError}
                </div>
              )}

              {/* List of Attachments */}
              {loadingAttachments ? (
                <p style={{ textAlign: "center", color: "#64748b" }}>Loading attachments...</p>
              ) : (
                <div className="tm-attachment-list">
                  {/* Saved attachments */}
                  {attachments.map((att) => (
                    <div key={att.id} className="tm-attachment-item">
                      <div className="tm-attachment-info">
                        {getFileIcon(att.mime_type, att.file_name)}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="tm-attachment-name" title={att.file_name}>
                            {att.file_name}
                          </div>
                          <div className="tm-attachment-meta">
                            {formatFileSize(att.file_size)} • Uploaded {new Date(att.uploaded_at).toLocaleDateString()}
                            {att.uploaded_by && ` by ${att.uploaded_by}`}
                          </div>
                        </div>
                      </div>

                      <div className="tm-attachment-actions">
                        <a
                          href={att.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="tm-attachment-btn download"
                          title="Download / View"
                        >
                          <FaDownload /> Download
                        </a>
                        <button
                          type="button"
                          className="tm-attachment-btn delete"
                          onClick={() => handleDeleteAttachment(att.id)}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Staged pending files (for new tasks not saved yet) */}
                  {pendingFiles.map((file, idx) => (
                    <div key={idx} className="tm-attachment-item" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                      <div className="tm-attachment-info">
                        {getFileIcon(file.type, file.name)}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="tm-attachment-name">{file.name}</div>
                          <div className="tm-attachment-meta">
                            {formatFileSize(file.size)} • <span style={{ color: "#16a34a", fontWeight: 600 }}>Ready to upload on save</span>
                          </div>
                        </div>
                      </div>
                      <div className="tm-attachment-actions">
                        <button
                          type="button"
                          className="tm-attachment-btn delete"
                          onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
                          title="Remove"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}

                  {attachments.length === 0 && pendingFiles.length === 0 && (
                    <div style={{ textAlign: "center", padding: "30px 10px", color: "#94a3b8" }}>
                      <FaPaperclip style={{ fontSize: "28px", marginBottom: "8px", opacity: 0.5 }} />
                      <p style={{ margin: 0, fontSize: "14px" }}>No attachments uploaded yet.</p>
                      <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#cbd5e1" }}>
                        Attached files are automatically indexed in Centralized Document Management.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* ACTIVITY HISTORY TAB */
            <div>
              {loadingActivities ? (
                <p style={{ textAlign: "center", color: "#64748b" }}>Loading activity history...</p>
              ) : activities.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px", color: "#64748b" }}>
                  <FaInfoCircle style={{ fontSize: "24px", marginBottom: "8px" }} />
                  <p>No activity records logged for this task yet.</p>
                </div>
              ) : (
                <div className="tm-activity-timeline">
                  {activities.map((act) => (
                    <div key={act.id} className="tm-activity-item">
                      <div className="tm-activity-dot" />
                      <div className="tm-activity-desc">{act.description}</div>
                      <div className="tm-activity-time">
                        By {act.performed_by || "System"} •{" "}
                        {new Date(act.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="tm-modal-footer">
          <button type="button" className="tm-btn tm-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="task-form"
            className="tm-btn tm-btn-primary"
            disabled={saving}
          >
            <FaSave /> {saving ? "Saving..." : task && task.id ? "Update Task" : "Create Task"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;
